import { NetworkStatus, useQuery, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  CurrencyAmountUI,
  cn,
  DataTable,
  DateUI,
  Icon,
  LabelPill,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  StatusUI,
  Txt,
  toast,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { formatCurrencyAmount, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useState } from "react";
import { RiBuilding2Line, RiFilter3Line, RiPriceTagLine } from "react-icons/ri";
import { z } from "zod";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { stripePayoutQueueItemStatusValues } from "~/gql/client/enumValues";
import type {
  StripePayoutQueueItemRowFragment,
  StripePayoutQueueItemsFilter,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const columns: ColumnDef<StripePayoutQueueItemRowFragment>[] = [
  {
    id: "date",
    header: "Arrival date",
    accessorKey: "arrivalDate",
    cell(c) {
      return <DateUI date={new Date(c.row.original.arrivalDate)} />;
    },
  },
  {
    id: "queueItemStatus",
    header: "Queue Status",
    accessorFn(c) {
      return c.status;
    },
    cell(c) {
      return <StatusUI status={c.row.original.status} />;
    },
  },
  {
    id: "organisation",
    header: "Organisation",
    accessorFn(c) {
      return c.organisation?.name;
    },
    cell(c) {
      return (
        <div className="flex max-w-32 flex-row items-center gap-2 overflow-hidden">
          <Icon
            imageUrl={c.row.original.organisation?.squareLogoUrl ?? undefined}
            fallbackIcon={<RiBuilding2Line />}
            size="6"
          />
          <p className="truncate">{c.row.original.organisation?.name}</p>
        </div>
      );
    },
  },
  {
    id: "grossAmount",
    header: "Gross amount",
    accessorFn(c) {
      return formatCurrencyAmount({ ...c.grossAmount, notation: "standard" });
    },
    cell(c) {
      return <CurrencyAmountUI ca={c.row.original.grossAmount} nonCopyable />;
    },
  },
  {
    id: "feeAmount",
    header: "Fee amount",
    accessorFn(c) {
      return formatCurrencyAmount({ ...c.feeAmount, notation: "standard" });
    },
    cell(c) {
      return <CurrencyAmountUI ca={c.row.original.feeAmount} nonCopyable />;
    },
  },
  {
    id: "netAmount",
    header: () => <span className="font-semibold">Net amount</span>,
    accessorFn(c) {
      return formatCurrencyAmount({ ...c.netAmount, notation: "standard" });
    },
    cell(c) {
      return (
        <div className="flex flex-row items-center gap-3">
          <strong>
            <CurrencyAmountUI ca={c.row.original.netAmount} nonCopyable />
          </strong>
          <div className="invisible group-hover/data-table-row:visible">
            <Button
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const amountToCopy = new Intl.NumberFormat("en-AU", {
                  useGrouping: false, // Removes thousands separators
                  minimumFractionDigits: 2, // Keeps decimal precision
                  maximumFractionDigits: 2,
                }).format(c.row.original.netAmount.amount.toNumber());
                navigator.clipboard.writeText(amountToCopy);
                toast({
                  kind: "success",
                  title: `${amountToCopy} copied to clipboard`,
                  autoHide: true,
                });
              }}
            >
              Copy
            </Button>
          </div>
        </div>
      );
    },
  },
  {
    id: "linkedMovement",
    header: "Linked transfer",
    accessorFn(c) {
      return c.transfer?.shortRef;
    },
    cell(c) {
      return <LinkedMovementLink item={c.row.original} />;
    },
  },
  {
    id: "linkedLedgerTransactions",
    header: "Linked transaction",
    accessorFn(c) {
      return c.ledgerTransactions?.map((txn) => txn.shortRef).join(", ");
    },
    cell(c) {
      return <LinkedLedgerTransactions item={c.row.original} />;
    },
  },
  {
    id: "stripePayoutStatus",
    header: "Stripe Payout Status",
    accessorFn(c) {
      return c.stripePayoutStatus;
    },
    cell(c) {
      return (
        <LabelPill
          variant={
            c.row.original.stripePayoutStatus === "PAID" ? "success" : "warning"
          }
          label={c.row.original.stripePayoutStatus ?? "UNKNOWN"}
        />
      );
    },
  },
];

export const Route = createFileRoute("/__auth/stripe-payout-queue")({
  head: () => opsHead("Stripe Payout Queue"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { useSearchParams } = useRouter();

  const [filter, setFilter] = useQueryPersistedStateWithSchema({
    useSearchParams,
    schema: z.object({
      organisationIds: z.array(z.string()).optional().nullable(),
      statuses: z
        .array(z.enum(stripePayoutQueueItemStatusValues))
        .optional()
        .nullable(),
    }),
  });

  const { data, fetchMore, loading, networkStatus } = useQuery(
    graphql(`
      query StripePayoutQueuePage($after: String, $first: Int!, $filter: StripePayoutQueueItemsFilter) {
        stripePayoutQueueItems(after: $after, first: $first, filter: $filter) {
          stripePayoutQueueItems {
            id
            ...StripePayoutQueueItemRow
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }

      fragment StripePayoutQueueItemRow on StripePayoutQueueItem {
        id
        organisation {
          id
          name
          squareLogoUrl
        }
        transfer {
          id
          shortRef
        }
        ledgerTransactions {
          id
          shortRef
        }
        grossAmount {
          currency
          amount
        }
        feeAmount {
          currency
          amount
        }
        netAmount {
          currency
          amount
        }
        status
        stripePayoutStatus
        arrivalDate
        stripeId
        createdAt
      }
    `),
    {
      variables: { after: null, first: 20, filter },
      notifyOnNetworkStatusChange: true,
    },
  );

  const items = data?.stripePayoutQueueItems?.stripePayoutQueueItems ?? [];
  const nextCursor = data?.stripePayoutQueueItems?.pageInfo.hasNextPage
    ? data.stripePayoutQueueItems.pageInfo.endCursor
    : undefined;
  const rows = items;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sidePaneVisible, setSidePaneVisible] = useState(false);
  const selectedItem = items.find((item) => item.id === selectedId);

  const { data: pageData } = useSuspenseQuery(
    graphql(`
      query AdminStripePayoutQueueFilters {
        organisations {
          id
          name
        }
      }
    `),
  );
  const organisationOptions = (
    pageData.organisations?.map((organisation) => ({
      value: organisation.id,
      label: organisation.name,
    })) ?? []
  ).sort((a, b) => a.label.localeCompare(b.label));

  return (
    <AdminPageContainer testId="stripe-payout-queue">
      <AdminPageHeader title="Stripe Payout Queue" />
      <div className="flex items-start gap-2">
        <div className="grid w-full flex-1 gap-6" style={{ zIndex: 1 }}>
          <FilterBar
            filterValue={filter}
            onFilterChange={setFilter}
            organisationOptions={organisationOptions}
          />

          <DataTable
            data={rows}
            activeRowId={
              sidePaneVisible ? (selectedId ?? undefined) : undefined
            }
            hidePagination={true}
            pageSize={10_000}
            onRowClick={(row) => {
              setSelectedId(row.original.id);
              setSidePaneVisible(
                sidePaneVisible ? row.original.id !== selectedId : true,
              );
            }}
            cellClassName="py-1 px-4"
            columns={columns}
          />
          {nextCursor ? (
            <Button
              variant="secondary"
              disabled={loading && networkStatus === NetworkStatus.fetchMore}
              onClick={() => {
                void fetchMore({
                  variables: { after: nextCursor, first: 20 },
                });
              }}
            >
              Load more
            </Button>
          ) : null}
        </div>

        <div
          className={cn(
            "absolute top-0 right-0 h-full w-1/2 transform overflow-auto border-l bg-primary p-8 shadow-popover duration-300 ease-in-out",
            sidePaneVisible
              ? "translate-x-0"
              : "pointer-events-none translate-x-full",
          )}
          style={{
            zIndex: 1,
          }}
        >
          {selectedItem ? (
            <div className="flex h-full flex-col gap-16">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-tertiary text-xs uppercase tracking-wide">
                    Stripe payout
                  </div>
                  <div className="mt-2 font-mono text-lg">
                    {selectedItem.stripeId}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <StatusUI status={selectedItem.status} />
                    <span className="text-tertiary text-xs">
                      {selectedItem.id}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<X />}
                  aria-label="Close"
                  onClick={() => {
                    setSidePaneVisible(false);
                  }}
                />
              </div>

              <div>
                <Txt variant="section-title">Payout summary</Txt>
                <div className="grid grid-cols-[264px_70%] gap-8">
                  <div>
                    <DetailRow
                      label="Gross amount"
                      value={<CurrencyAmountUI ca={selectedItem.grossAmount} />}
                    />
                    <DetailRow
                      label="Fee amount"
                      value={<CurrencyAmountUI ca={selectedItem.feeAmount} />}
                    />
                    <DetailRow
                      label="Net amount"
                      value={<CurrencyAmountUI ca={selectedItem.netAmount} />}
                    />
                  </div>
                  <div>
                    <DetailRow
                      label="Organisation"
                      value={
                        selectedItem.organisation ? (
                          <Link
                            to={`/organisations/${selectedItem.organisation.id}`}
                            className="hover:underline"
                          >
                            <div className="flex max-w-32 flex-row items-center gap-2 overflow-hidden">
                              <Icon
                                imageUrl={
                                  selectedItem.organisation?.squareLogoUrl ??
                                  undefined
                                }
                                fallbackIcon={<RiBuilding2Line />}
                                size="5"
                              />
                              <p className="truncate">
                                {selectedItem.organisation?.name}
                              </p>
                            </div>
                          </Link>
                        ) : (
                          "-"
                        )
                      }
                    />
                    <DetailRow
                      label="Arrival date"
                      value={
                        <DateUI date={new Date(selectedItem.arrivalDate)} />
                      }
                    />
                    <DetailRow
                      label="Stripe ID"
                      value={
                        <div className="overflow-hidden">
                          <p className="truncate font-mono text-sm">
                            {selectedItem.stripeId}
                          </p>
                        </div>
                      }
                    />
                    <DetailRow
                      label="Linked transfer"
                      value={<LinkedMovementLink item={selectedItem} />}
                    />
                    <DetailRow
                      label="Linked transaction"
                      value={<LinkedLedgerTransactions item={selectedItem} />}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-tertiary">
              Select a payout to see details.
            </div>
          )}
        </div>
        {sidePaneVisible && (
          <div
            className="absolute top-0 left-0 h-full w-full overflow-hidden"
            style={{
              zIndex: 0,
            }}
            onClick={() => {
              setSidePaneVisible(false);
            }}
          ></div>
        )}
      </div>
    </AdminPageContainer>
  );
}

function FilterBar({
  filterValue,
  onFilterChange,
  organisationOptions,
}: {
  filterValue: StripePayoutQueueItemsFilter;
  onFilterChange: (value: StripePayoutQueueItemsFilter) => void;
  organisationOptions: { value: string; label: string }[];
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("status");
  const filterCount =
    (filterValue.statuses?.length ? 1 : 0) +
    (filterValue.organisationIds?.length ? 1 : 0);

  return (
    <div className="flex w-full flex-row gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            leftIcon={<RiFilter3Line />}
            variant="outline"
            size="sm"
            className="mb-4 h-8 w-fit text-primary"
          >
            Filter{" "}
            {filterCount > 0 ? (
              <span className="ml-2 text-info">({filterCount})</span>
            ) : null}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex h-[500px] w-[760px] p-0" align="start">
          <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("status")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Status</div>
                {(filterValue.statuses?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("organisation")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Organisation</div>
                {(filterValue.organisationIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info" />
                )}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                onFilterChange({
                  statuses: undefined,
                  organisationIds: undefined,
                })
              }
            >
              Reset all filters
            </Button>
          </div>
          <div className="flex-1">
            {path === "status" && (
              <ControlledFacetedFilterControl
                options={stripePayoutQueueItemStatusValues.map((status) => ({
                  value: status,
                  label: prettyPrintEnum(status),
                }))}
                selectedValues={filterValue.statuses ?? []}
                onChange={(statuses) =>
                  onFilterChange({
                    ...filterValue,
                    statuses: statuses.length ? statuses : undefined,
                  })
                }
              />
            )}
            {path === "organisation" && (
              <ControlledFacetedFilterControl
                options={organisationOptions}
                selectedValues={filterValue.organisationIds ?? []}
                onChange={(organisationIds) =>
                  onFilterChange({
                    ...filterValue,
                    organisationIds: organisationIds.length
                      ? organisationIds
                      : undefined,
                  })
                }
              />
            )}
          </div>
        </PopoverContent>
      </Popover>
      <ControlledFactedFilterSummary
        title="Status"
        options={stripePayoutQueueItemStatusValues.map((status) => ({
          value: status,
          label: prettyPrintEnum(status),
        }))}
        selectedValues={filterValue.statuses ?? []}
        onClick={() => {
          setPath("status");
          setOpen(true);
        }}
      />
      <ControlledFactedFilterSummary
        title="Organisation"
        options={organisationOptions}
        selectedValues={filterValue.organisationIds ?? []}
        onClick={() => {
          setPath("organisation");
          setOpen(true);
        }}
      />
    </div>
  );
}

function LinkedMovementLink({
  item,
}: {
  item: StripePayoutQueueItemRowFragment;
}) {
  if (item.transfer) {
    return (
      <Link
        to={`/transfers/${item.transfer.id}`}
        className="font-mono text-sm hover:underline"
      >
        {item.transfer.shortRef}
      </Link>
    );
  }

  return <span>-</span>;
}

function LinkedLedgerTransactions({
  item,
}: {
  item: StripePayoutQueueItemRowFragment;
}) {
  if (!item.ledgerTransactions || item.ledgerTransactions.length === 0) {
    return <span>-</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      {item.ledgerTransactions.map((transaction) => (
        <Link
          key={transaction.id}
          to={`/ledger/transactions/${transaction.id}`}
          className="font-mono text-sm hover:underline"
        >
          {transaction.shortRef}
        </Link>
      ))}
    </div>
  );
}
